import { isBaseAsset } from './thorChainUtils'

/**
 * Utility functions for parsing and working with ThorChain asset formats
 */

/**
 * Get token contract address from THORChain asset format
 * @param thorChainAsset - Asset in format like "ETH.USDT-0xdAC17F958D2ee523a2206206994597C13D831ec7"
 * @returns Contract address or null if not a token or invalid format
 */
export const getTokenContractAddress = (
  thorChainAsset?: string
): string | null => {
  if (!thorChainAsset) return null
  const parts = thorChainAsset.split('.')
  if (parts.length > 1 && parts[1].includes('-')) {
    const tokenPart = parts[1].split('-')
    if (tokenPart.length > 1) {
      let address = tokenPart[1]
      // Normalize the address format
      if (address.toUpperCase().startsWith('0X')) {
        address = '0x' + address.slice(2).toLowerCase()
      }
      // Check if it looks like an Ethereum address (42 characters total)
      if (address.startsWith('0x') && address.length === 42) {
        return address
      }
    }
  }
  return null
}

/**
 * Get the chain identifier from a ThorChain asset
 * @param thorChainAsset - Asset in format like "ETH.USDT-0x..." or "AVAX.AVAX"
 * @returns Chain identifier (e.g., "ETH", "AVAX") or null
 */
export const getAssetChainFromThorChainAsset = (
  thorChainAsset?: string
): string | null => {
  if (!thorChainAsset) return null
  const parts = thorChainAsset.split('.')
  return parts.length >= 2 ? parts[0] : null
}

/**
 * Check if a ThorChain asset is a base asset (native token) or ERC-20 token
 * @param thorChainAsset - Asset in ThorChain format
 * @returns Object with asset type information
 */
export const getThorChainAssetInfo = (thorChainAsset?: string) => {
  if (!thorChainAsset) {
    return {
      isBaseAsset: false,
      tokenContractAddress: null,
      chain: null,
      symbol: null,
    }
  }

  const chain = getAssetChainFromThorChainAsset(thorChainAsset)
  const isBase = isBaseAsset(thorChainAsset)
  const tokenAddress = isBase ? null : getTokenContractAddress(thorChainAsset)

  // Extract symbol from asset format
  const parts = thorChainAsset.split('.')
  const symbol = parts.length >= 2 ? parts[1].split('-')[0] : null

  return {
    isBaseAsset: isBase,
    tokenContractAddress: tokenAddress,
    chain,
    symbol,
  }
}

/**
 * Check if balance should be displayed for a given asset and network combination
 * @param thorChainAsset - The ThorChain asset
 * @param connectedNetworkIsoCode - The currently connected network
 * @param hasWalletAddress - Whether a wallet is connected
 * @returns Whether balance should be shown
 */
export const shouldShowBalanceForAsset = (
  thorChainAsset?: string,
  connectedNetworkIsoCode?: string,
  hasWalletAddress = false
): boolean => {
  if (!hasWalletAddress || !thorChainAsset || !connectedNetworkIsoCode) {
    return false
  }

  const assetInfo = getThorChainAssetInfo(thorChainAsset)

  // Only show balance if asset chain matches connected network
  // and we have either a base asset or a valid token contract address
  return !!(
    assetInfo.chain === connectedNetworkIsoCode &&
    (assetInfo.isBaseAsset || assetInfo.tokenContractAddress)
  )
}
